<template>
  <div>
    <template v-if="!isMobile">
      <table
        class="w-full border-separate border-spacing-0"
      >
        <thead class="sticky top-0 z-10 bg-white">
          <tr>
            <template
              v-for="column in columns"
              :key="column.id || column.header"
            >
              <th
                :class="[
                  'border-b border-background-fg/8 bg-white p-2',
                  column.align
                    ? {
                        left: 'text-left',
                        right: 'text-right',
                        center: 'text-center',
                      }[column.align]
                    : 'text-left',
                  ,
                  column.isSortable ? 'cursor-pointer' : '',
                ]"
                @click="onClickHeader(column)"
              >
                <div class="flex items-center space-x-2">
                  <span
                    class="whitespace-nowrap text-left text-sm font-normal text-background-fg/70"
                  >
                    {{ column.header || "" }}
                  </span>
                  <template v-if="column.isSortable">
                    <template v-if="ordering === column.id">
                      <IconArrowUp class="size-3" />
                    </template>
                    <template
                      v-else-if="
                        ordering === `-${column.id}`
                      "
                    >
                      <IconArrowDown class="size-3" />
                    </template>
                    <template v-else>
                      <div class="size-3" />
                    </template>
                  </template>
                </div>
              </th>
            </template>
            <th />
          </tr>
        </thead>
        <tbody class="relative">
          <BaseInnerLoading v-if="isLoading" />
          <template
            v-for="(row, rowIndex) in rows"
            :key="rowIndex"
          >
            <tr
              :class="getRowClass ? getRowClass(row) : ''"
            >
              <template
                v-for="column in columns"
                :key="column.id || column.header"
              >
                <td
                  :class="[
                    `border-b border-background-fg/5 px-3 py-2 text-sm font-medium`,
                    column.autoWidth
                      ? 'w-1 whitespace-nowrap'
                      : '',
                    column.align === 'right'
                      ? 'text-right'
                      : column.align === 'left'
                        ? 'text-left'
                        : '',
                    column.cellClass,
                  ]"
                >
                  <slot
                    :name="`cell-${column.id}`"
                    v-bind="{
                      row,
                      rowIndex,
                      value:
                        column.getCellValue &&
                        column.getCellValue(row),
                    }"
                  >
                    {{
                      column.getCellValue &&
                      column.getCellValue(row)
                    }}
                  </slot>
                </td>
              </template>
            </tr>
          </template>
          <tr v-if="rows.length === 0">
            <td colspan="100%" class="text-center">
              <span v-if="isLoading">&nbsp;</span>
              <span v-else>{{
                noRowsText ?? "No Results"
              }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <template v-else>
      <ul class="relative space-y-2 overflow-y-auto p-1">
        <BaseInnerLoading v-if="isLoading" />
        <template
          v-for="(row, rowIndex) in rows"
          :key="rowIndex"
        >
          <li>
            <slot
              name="grid-card"
              v-bind="{ row, rowIndex, value: undefined }"
            >
              <dl class="space-y-2 p-2 shadow">
                <template
                  v-for="column in columns"
                  :key="column.id || column.header"
                >
                  <div
                    v-if="!column.desktopOnly"
                    class="flex items-center justify-between"
                  >
                    <dt class="text-xs">
                      {{ column.header }}
                    </dt>
                    <dd
                      class="text-sm font-semibold text-background-fg/87"
                    >
                      <slot
                        :name="`cell-${column.id}`"
                        v-bind="{
                          row,
                          rowIndex,
                          value:
                            column.getCellValue &&
                            column.getCellValue(row),
                        }"
                      >
                        {{
                          column.getCellValue &&
                          column.getCellValue(row)
                        }}
                      </slot>
                    </dd>
                  </div>
                </template>
              </dl>
            </slot>
          </li>
        </template>
        <li v-if="rows.length === 0">
          <div class="p-2 text-center shadow">
            <span v-if="isLoading">&nbsp;</span>
            <span v-else>{{
              noRowsText ?? "No Results"
            }}</span>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    TRow extends object,
    TColumnId extends string &
      (string extends TColumnId ? never : unknown), // This allows string literal unions but not 'string' itself
    TOrderingColumnId extends TColumnId = never
  "
>
export type TableColumn<
  TR extends object,
  TC extends string & (string extends TC ? never : unknown),
  TO extends TC = never,
> =
  | {
      id: Extract<TC, TO>
      header?: string
      getCellValue?: (row: TR) => any
      autoWidth?: boolean
      align?: "right" | "left" | "center"
      isSortable: true
      desktopOnly?: boolean
      cellClass?: string
    }
  | {
      id: Exclude<TC, TO>
      header?: string
      getCellValue?: (row: TR) => any
      autoWidth?: boolean
      align?: "right" | "left" | "center"
      isSortable?: false
      desktopOnly?: boolean
      cellClass?: string
    }

export type TableOrdering<TO extends string> = TO | `-${TO}`

const props = defineProps<{
  columns: Array<
    TableColumn<TRow, TColumnId, TOrderingColumnId>
  >
  rows: Array<TRow> | ReadonlyArray<TRow>
  isLoading?: boolean
  noRowsText?: string
  getRowClass?: (row: TRow) => string
}>()

const ordering =
  defineModel<TableOrdering<TOrderingColumnId>>("ordering")

type SlotName = `cell-${TColumnId}` | "grid-card"

defineSlots<{
  [N in SlotName]: (props: {
    row: TRow
    rowIndex: number
    value: any
  }) => any
}>()

const isMobile = useIsMobile()

const onClickHeader = (
  column: TableColumn<TRow, TColumnId, TOrderingColumnId>
) => {
  if (!column.isSortable) return

  if (ordering.value === column.id) {
    ordering.value = `-${column.id}`
  } else if (ordering.value === `-${column.id}`) {
    ordering.value = undefined
  } else {
    ordering.value = column.id
  }
}
</script>
